import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { usePremium } from "@/lib/premium";
import { checkProAccess } from "@/lib/pro/pipeline.functions";

type CheckoutSearch = {
  session_id?: string;
  plan: "premium" | "pro";
  upgraded: boolean;
};

export const Route = createFileRoute("/checkout/return")({
  validateSearch: (search: Record<string, unknown>): CheckoutSearch => ({
    session_id: typeof search.session_id === "string" ? search.session_id : undefined,
    plan: search.plan === "pro" ? "pro" : "premium",
    upgraded: search.upgraded === "1" || search.upgraded === true,
  }),
  component: CheckoutReturn,
});

function CheckoutReturn() {
  const { session_id, plan, upgraded } = Route.useSearch();
  const { isPremium, refetch } = usePremium();
  const checkPro = useServerFn(checkProAccess);
  const navigate = useNavigate();
  const [confirmed, setConfirmed] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const hasReturnContext = Boolean(session_id || upgraded);

  useEffect(() => {
    if (plan === "premium" && isPremium) setConfirmed(true);
  }, [isPremium, plan]);

  useEffect(() => {
    if (!hasReturnContext || confirmed) return;
    let cancelled = false;
    let timer: number | undefined;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      try {
        if (plan === "pro") {
          const result = await checkPro();
          if (result.hasPro) {
            setConfirmed(true);
            return;
          }
        } else {
          await refetch();
        }
      } catch {
        // Webhook/replication may still be completing; retry below.
      }
      attempts += 1;
      if (attempts >= 12) {
        setTimedOut(true);
        return;
      }
      timer = window.setTimeout(tick, 1500);
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [checkPro, confirmed, hasReturnContext, plan, refetch]);

  useEffect(() => {
    if (!confirmed) return;
    const target = plan === "pro" ? "/pro" : "/";
    const timer = window.setTimeout(() => navigate({ to: target }), 2500);
    return () => window.clearTimeout(timer);
  }, [confirmed, navigate, plan]);

  return (
    <main className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center px-4 text-center">
      {confirmed ? (
        <>
          <CheckCircle2 className="h-14 w-14 text-brand" />
          <h1 className="mt-4 text-2xl font-semibold">
            {plan === "pro" ? "Routify Pro ativo!" : "Routify Premium ativo!"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            O pagamento foi confirmado e o teu plano já está disponível.
          </p>
          <Link
            to={plan === "pro" ? "/pro" : "/"}
            className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            {plan === "pro" ? "Abrir o Pro" : "Ir para as rotas"} <ArrowRight className="h-4 w-4" />
          </Link>
        </>
      ) : !hasReturnContext ? (
        <>
          <h1 className="text-xl font-semibold">Sessão não encontrada</h1>
          <Link to="/" className="mt-4 text-sm underline">Voltar ao início</Link>
        </>
      ) : timedOut ? (
        <>
          <h1 className="text-xl font-semibold">O pagamento está a ser processado</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Não voltes a pagar. Atualiza esta página dentro de alguns instantes; o Stripe continuará a confirmação automaticamente.
          </p>
          <button onClick={() => window.location.reload()} className="mt-5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
            Verificar novamente
          </button>
        </>
      ) : (
        <>
          <Loader2 className="h-10 w-10 animate-spin text-brand" />
          <h1 className="mt-4 text-xl font-semibold">A confirmar o pagamento…</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Estamos a ativar o teu plano. Isto demora apenas alguns segundos.
          </p>
        </>
      )}
    </main>
  );
}
